import React from 'react';
import styled from 'styled-components';
import type { AnsweringServicePayer } from '@/app-package';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import {
  SupraSubscriptionProductBillingModel,
  SupraSystemSubscriptionStatus,
} from '@bb/domain/dist/types';
import { ViewingLevel } from 'Router/pages/SubscriptionsPaymentsPage';
import OrgPlanCard, { PlanTier, OrgPlanPayer } from './OrgPlanCard';
import AnsweringServiceCard from './AnsweringServiceCard';
import SupraOneCard, { useSupraSubscriptionStatus } from './SupraOneCard';

export interface CurrentServicesSectionProps {
  /** What type of Answering Service this user/brokerge has (or is eligible to sign up for) */
  answeringServiceType: AnsweringServiceType;
  /** Who pays for the Answering Service subscription */
  answeringServicePayer: AnsweringServicePayer;
  /** Whether the SUPRA_MVP flag is turned on granting access to all Supra One features for this org. */
  canUseSupraOne: boolean;
  /** The type of Org Plan this brokerage is on. Agent Plan (Tier 1) or Brokerage Plan (Tier 2). */
  currentOrgPlan: PlanTier;
  /** Whether there was an error fetching the Answering Service subscription data. */
  fetchErrorAnsweringService: boolean;
  /** Whether there was an error fetching who pays for the Supra One subscription. */
  fetchErrorSupraOne: boolean;
  /** Whether there is an already active Answering Service subscription for this user/brokerage */
  hasActiveAnsweringService: boolean;
  /** Callback when Live Concierge View Details is clicked */
  onAnsweringServiceClick: () => void;
  /** Callback when Org Plan View Details/Learn More is clicked */
  onOrgPlanClick: () => void;
  /** Callback when Supra One View Details is clicked */
  onSupraOneClick: () => void;
  /** How the Org Plan subscription is billed */
  orgPlanPayer: OrgPlanPayer;
  /** Who pays for the Supra One subscription */
  supraSubscriptionPayer: SupraSubscriptionProductBillingModel;
  /** Whether this is an indivdual user or for a brokerage viewing the page */
  viewingLevel: ViewingLevel;
}

/** Statuses that should render in the Current Services section */
const CURRENT_SERVICES_SUPRA_STATUSES: readonly SupraSystemSubscriptionStatus[] =
  [
    SupraSystemSubscriptionStatus.subscribed,
    SupraSystemSubscriptionStatus.suspended,
    SupraSystemSubscriptionStatus.pending,
  ] as const;

/**
 * The "Current Services" section of the Subscription & Payments page
 * displaying all of the user/brokerage's active subscriptions.
 */
const CurrentServicesSection: React.FC<CurrentServicesSectionProps> = ({
  answeringServicePayer,
  answeringServiceType = AnsweringServiceType.LiveConciergePlus,
  canUseSupraOne,
  currentOrgPlan,
  hasActiveAnsweringService = false,
  fetchErrorAnsweringService,
  fetchErrorSupraOne,
  onAnsweringServiceClick,
  onOrgPlanClick,
  onSupraOneClick,
  orgPlanPayer,
  supraSubscriptionPayer,
  viewingLevel,
}) => {
  const supraSubscriptionStatus = useSupraSubscriptionStatus();

  const showSupraCard =
    canUseSupraOne &&
    CURRENT_SERVICES_SUPRA_STATUSES.includes(supraSubscriptionStatus);

  const hasAnyCurrentServices =
    currentOrgPlan || hasActiveAnsweringService || showSupraCard;

  // If the user has no subscriptions being paid for, don't display anything
  if (!hasAnyCurrentServices) {
    return null;
  }

  return (
    <Container>
      <Title>Current Services</Title>
      <CardsContainer>
        <OrgPlanCard
          currentOrgPlan={currentOrgPlan}
          orgPlanPayer={orgPlanPayer}
          viewingLevel={viewingLevel}
          onButtonClick={onOrgPlanClick}
        />
        {hasActiveAnsweringService &&
          answeringServiceType !== AnsweringServiceType.Legacy && (
            <AnsweringServiceCard
              fetchDataError={fetchErrorAnsweringService}
              isSubscribed
              answeringServiceType={answeringServiceType}
              answeringServicePayer={answeringServicePayer}
              onButtonClick={onAnsweringServiceClick}
            />
          )}
        {showSupraCard && (
          <SupraOneCard
            fetchDataError={fetchErrorSupraOne}
            supraSubscriptionPayer={supraSubscriptionPayer}
            onButtonClick={onSupraOneClick}
          />
        )}
      </CardsContainer>
    </Container>
  );
};

export default CurrentServicesSection;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #2b2b2b;
  margin: 0;
`;

const CardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
